import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl, dollars } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { Play, Sparkles } from "lucide-react";
import ExplicitBadge from "@/components/ExplicitBadge";

/**
 * RecentReleases — a horizontal-scrolling strip of the newest tracks in the
 * past 7 days. Lives on the homepage below the stats strip so first-time
 * visitors immediately see fresh material without hunting.
 *
 * Cards are compact (cover + title + artist link + play) and the whole
 * strip hides when there's nothing new — no ghost sections.
 */
export default function RecentReleases() {
  const [tracks, setTracks] = useState(null);
  const { setQueue, play } = usePlayer();

  useEffect(() => {
    api.get("/recent-tracks?days=7&limit=12")
      .then((r) => setTracks(r.data.tracks || []))
      .catch(() => setTracks([]));
  }, []);

  if (!tracks || tracks.length === 0) return null;

  const start = (t) => { setQueue(tracks); play(t); };

  return (
    <section data-testid="recent-releases" className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
      <div className="flex items-center gap-2 mb-5">
        <Sparkles className="h-5 w-5 text-[#FF4500]" />
        <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tighter">Just released</h2>
        <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500 ml-2">
          Last 7 days
        </span>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-3 -mx-4 px-4 md:mx-0 md:px-0 snap-x snap-mandatory">
        {tracks.map((t) => (
          <button
            key={t.id}
            data-testid={`recent-card-${t.id}`}
            onClick={() => start(t)}
            className="group text-left w-40 md:w-52 shrink-0 snap-start border border-[#2A2A2A] bg-[#121212] hover:border-[#FF4500] transition-colors"
          >
            <div className="relative aspect-square overflow-hidden bg-neutral-900">
              {t.cover_path
                ? <img src={fileUrl(t.cover_path)} alt={t.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                : <div className="h-full w-full grid place-items-center text-gray-700 font-display text-3xl">♪</div>}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center">
                <Play className="h-10 w-10 text-[#FF4500] fill-current" />
              </div>
            </div>
            <div className="p-3">
              <div className="font-display font-bold text-sm truncate flex items-center gap-1.5">
                <span className="truncate">{t.title}</span>
                <ExplicitBadge explicit={t.is_explicit} trackId={t.id} size="xs" />
              </div>
              <Link
                to={`/artist/${t.artist_id}`}
                onClick={(e) => e.stopPropagation()}
                className="font-mono text-[10px] uppercase tracking-widest text-gray-500 hover:text-[#FF4500] truncate block mt-1"
              >
                {t.artist_name}
              </Link>
              <div className="font-mono text-[10px] text-[#FF4500] mt-1">
                {t.is_free ? "FREE" : dollars(t.price_cents || 99)}
              </div>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
